import React from 'react';
import { ActivityIndicator, Image, Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';

import { DashboardScrollView } from '@/components/dashboard-scroll-view';
import { ThemedText } from '@/components/themed-text';
import { EmptyState } from '@/components/ui/empty-state';
import { PressableCard } from '@/components/ui/card';
import { useBookableProviders } from '@/hooks/use-appointments';
import { BookableProvider } from '@/types/appointment';

const DEFAULT_BRAND = '#FD5001';

function ProviderRow({ provider }: { provider: BookableProvider }) {
  const brand = provider.brandColor || DEFAULT_BRAND;
  const open = () => {
    if (provider.slug) {
      router.push(`/@${provider.slug}` as never);
    } else {
      router.push({
        pathname: '/(tabs)/(client)/booking/provider/[providerId]',
        params: { providerId: provider.id, name: provider.name },
      });
    }
  };

  return (
    <PressableCard onPress={open} className="w-full flex-row items-center gap-4 p-4">
      <View
        className="h-12 w-12 items-center justify-center overflow-hidden rounded-2xl"
        style={{ backgroundColor: `${brand}1A`, borderCurve: 'continuous' }}
      >
        {provider.logoUrl ? (
          <Image source={{ uri: provider.logoUrl }} aria-hidden style={{ width: 48, height: 48 }} />
        ) : (
          <ThemedText className="text-lg font-bold" style={{ color: brand }}>
            {provider.name.slice(0, 1).toUpperCase()}
          </ThemedText>
        )}
      </View>
      <View className="min-w-0 flex-1 gap-0.5">
        <ThemedText className="font-semibold text-foreground" numberOfLines={1}>
          {provider.name}
        </ThemedText>
        {!!provider.category && (
          <ThemedText className="text-sm text-neutral-500" numberOfLines={1}>
            {provider.category}
          </ThemedText>
        )}
      </View>
      <Ionicons className="text-neutral-400" name="chevron-forward" size={18} />
    </PressableCard>
  );
}

export default function BookingHubScreen() {
  const { data, isLoading } = useBookableProviders();

  return (
    <View className="flex-1 bg-canvas">
      <DashboardScrollView
        className="w-full"
        contentContainerStyle={{ paddingBottom: 80 }}
      >
        <View className="gap-6 p-5">
          <View className="flex-row items-center gap-3">
            <Pressable
              onPress={() => router.back()}
              className="h-10 w-10 items-center justify-center rounded-full bg-card"
              accessibilityLabel="Voltar"
            >
              <Ionicons className="text-neutral-400" name="arrow-back" size={20} />
            </Pressable>
            <View className="flex-1">
              <ThemedText className="text-xl font-bold">Agendar horário</ThemedText>
              <ThemedText className="text-sm text-neutral-500">
                Escolha um prestador para ver a agenda
              </ThemedText>
            </View>
          </View>

          <PressableCard
            onPress={() => router.push('/(tabs)/(client)/appointments')}
            className="w-full flex-row items-center gap-4 border-primary-100 bg-primary-50 p-4"
          >
            <View className="h-11 w-11 items-center justify-center rounded-2xl bg-card">
              <Ionicons name="calendar-outline" size={20} color="#FD5001" />
            </View>
            <ThemedText className="flex-1 font-semibold text-foreground">
              Meus agendamentos
            </ThemedText>
            <Ionicons name="chevron-forward" size={18} color="#FD5001" />
          </PressableCard>

          {isLoading ? (
            <View className="items-center py-16">
              <ActivityIndicator size="large" color="#FD5001" />
            </View>
          ) : (data?.length ?? 0) === 0 ? (
            <EmptyState
              icon="storefront-outline"
              title="Nenhum prestador com agenda aberta"
              description="Quando um prestador ativar a agenda online, ele aparece aqui. Você também pode abrir o link do perfil dele para agendar."
            />
          ) : (
            <View className="gap-3">
              <ThemedText className="text-sm font-semibold text-neutral-500">
                Prestadores
              </ThemedText>
              {data!.map((p) => (
                <ProviderRow key={p.id} provider={p} />
              ))}
            </View>
          )}
        </View>
      </DashboardScrollView>
    </View>
  );
}
